// Today's plays and how they were earned (mockup r03, provisional until the owner picks): the
// rules are on show, so a child knows how to earn more. Done lines get a ✓; the daily goal and
// today's activities that still wait show what's left to do.
import { useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { closeOverlay } from '../app/router'
import { Sheet } from '../app/Sheet'
import { useSession } from '../data/session'
import { record } from '../data/events'
import { rightOn, type PlayLine } from '../domain/arcade/plays'
import { phaseOn } from '../domain/release'
import { taskPool, tasksFor } from '../domain/tasks'
import { tripNow } from '../../shared/tripZone'
import { t } from '../strings/en'
import { useArcade } from './useArcade'

/**
 * Another grown-up to ask (WI-37: never `me`, who may be a grown-up who plays): the parent, else
 * the other grown-up; null when there's nobody, as for a solo traveller.
 */
export function useOtherGrownUp(me?: string): string | null {
  const people = useSession((s) => s.people)
  const others = people?.filter((p) => p.kind !== 'child' && p.id !== me) ?? []
  return (others.find((p) => p.kind === 'admin') ?? others[0])?.name ?? null
}

/** The parent's name for "Bonus from Dad", or "a grown-up". */
export function useGrownUpName(me?: string): string {
  return useOtherGrownUp(me) ?? t.plays.grownUp
}

interface Row {
  key: string
  icon: string
  title: string
  sub: string
  tag: { cls: string; text: string }
}

const DONE = { cls: 'tag mint', text: '✓' }

export function PlaysSheet({ pack, person }: { pack: Pack; person: Person }) {
  const { plays, day, rules, settings, events } = useArcade(pack, person.id)
  const asks = useOtherGrownUp(person.id)
  const grownUp = asks ?? t.plays.grownUp
  const [today] = useState(() => tripNow(pack, Date.now()).date)

  const line = (l: PlayLine): Row => {
    const key = `${l.trigger}:${l.ref}`
    switch (l.trigger) {
      case 'free':
        return { key, icon: '🎁', title: t.plays.free, sub: t.plays.freeSub(l.plays), tag: DONE }
      case 'daily':
        return { key, icon: '☀️', title: t.plays.goal(settings?.learn.goal ?? 0, settings?.learn.goal ?? 0), sub: t.plays.goalDoneSub(l.plays), tag: DONE }
      case 'checkin':
        return { key, icon: '📍', title: t.plays.checkin(pack.places.find((p) => p.id === l.ref)?.name ?? l.ref), sub: t.plays.checkinSub(l.plays), tag: DONE }
      case 'activity':
        return { key, icon: '⭐', title: pack.itinerary.dayItems.find((i) => i.id === l.ref)?.title ?? l.ref, sub: t.plays.activitySub(l.plays), tag: DONE }
      case 'task': {
        const task = pool.find((x) => x.id === l.ref)
        return { key, icon: task?.emoji ?? '🧳', title: task?.title ?? l.ref, sub: t.plays.taskDoneSub(l.plays), tag: DONE }
      }
      case 'bonus':
        return { key, icon: '🎉', title: t.plays.bonus(grownUp), sub: t.plays.bonusSub(l.plays), tag: DONE }
    }
  }
  const pool = taskPool(events)
  const rows = plays.lines.map(line)
  // Holiday tasks still to do (WI-13b): self-ticked ones have a tick button; others ask a grown-up.
  const player = useSession((s) => s.people?.find((p) => p.id === person.id))
  const tasks = settings && player && rules.tasks?.on !== false ? tasksFor(events, player, phaseOn({ start: pack.meta.startDate, end: pack.meta.endDate }, day)).filter((x) => x.state !== 'done') : []
  const tick = (taskId: string, self: boolean) =>
    void record(self ? 'task.done' : 'task.asked', { taskId, for: person.id, day }, person.id)

  // Still to earn today (players only): the goal, and activities waiting for a grown-up.
  if (settings) {
    if (rules.daily.on && !plays.lines.some((l) => l.trigger === 'daily')) {
      const done = rightOn(events, person.id, day)
      rows.push({ key: 'goal', icon: '☀️', title: t.plays.goal(done, settings.learn.goal), sub: t.plays.goalTodoSub(rules.daily.plays), tag: { cls: 'tag dash', text: t.plays.todo } })
    }
    const awarded = new Set(plays.lines.filter((l) => l.trigger === 'activity').map((l) => l.ref))
    // Nobody to ask (just me): an activity can't be confirmed, so it isn't offered.
    for (const i of asks ? pack.itinerary.dayItems : []) {
      if (!i.award || i.date !== today || awarded.has(i.id) || (i.who && !i.who.includes(person.id))) continue
      rows.push({ key: `ask:${i.id}`, icon: '⭐', title: i.title, sub: t.plays.activityAsk(grownUp, i.award.plays), tag: { cls: 'tag yellow', text: t.plays.ask } })
    }
  }

  return (
    <Sheet title={t.arcade.playsToday(plays.left)}>
      <p className="mono sheet-sub split">
        <span>{t.plays.howEarned}</span>
        <span>{t.plays.most(plays.cap)}</span>
      </p>
      <div className="panel sheet-panel">
        {plays.carried ? (
          <div className="row">
            <span className="fam-av">💾</span>
            <div>
              <b>{t.plays.carried(plays.carried)}</b>
            </div>
          </div>
        ) : null}
        {rows.map((r) => (
          <div className="row" key={r.key}>
            <span className="fam-av">{r.icon}</span>
            <div>
              <b>{r.title}</b>
              <small>{r.sub}</small>
            </div>
            <span className={r.tag.cls}>{r.tag.text}</span>
          </div>
        ))}
      </div>
      {plays.waiting ? <p className="caption">{t.plays.waiting(plays.waiting)}</p> : null}
      {tasks.length ? (
        <>
          <p className="mono sheet-sub split">
            <span>{t.plays.tasks}</span>
          </p>
          <div className="panel sheet-panel tasks-panel">
            {tasks.map((x) => (
              <div className="row" key={x.id} data-task={x.id}>
                <span className="fam-av">{x.emoji}</span>
                <div>
                  <b>{x.title}</b>
                  <small>{x.state === 'asked' ? t.plays.taskAsked(grownUp) : x.self ? t.plays.taskSelf(x.plays) : t.plays.taskAsk(grownUp, x.plays)}</small>
                </div>
                {x.state === 'asked' ? (
                  <span className="tag yellow">{t.plays.ask}</span>
                ) : x.self || asks ? (
                  <button className="btn sm press task-tick" onClick={() => tick(x.id, x.self)}>
                    {x.self ? t.plays.tickDone : t.plays.tickAsk}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
      <button className="btn y press" style={{ marginTop: 16 }} onClick={closeOverlay}>
        {t.plays.toMachine}
      </button>
    </Sheet>
  )
}
